<script setup lang="ts">
/**
 * UseCases — 3 个使用场景卡
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

const { tm } = useI18n()

interface CaseItem {
  icon: IconName
  title: string
  desc: string
}

const cases = computed<CaseItem[]>(() => {
  const ref = tm('home.useCases.cases') as any
  return ref?.value ?? ref ?? []
})
</script>

<template>
  <div class="usecases">
    <article
      v-for="(c, idx) in cases"
      :key="idx"
      class="usecases__card"
    >
      <div class="usecases__icon">
        <YfbIcon :name="c.icon" :size="32" />
      </div>
      <h3 class="usecases__title">{{ c.title }}</h3>
      <p class="usecases__desc">{{ c.desc }}</p>
      <div class="usecases__num">{{ String(idx + 1).padStart(2, '0') }}</div>
    </article>
  </div>
</template>

<style scoped>
.usecases {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--yfb-sp-6);
  max-width: 1200px;
  margin: 0 auto;
}

.usecases__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-7) var(--yfb-sp-6) var(--yfb-sp-6);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  overflow: hidden;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.usecases__card:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

/* Card 1：accent 渐变底 */
.usecases__card:nth-child(1) {
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
}

.usecases__card:nth-child(1) .usecases__title,
.usecases__card:nth-child(1) .usecases__desc {
  color: var(--yfb-on-accent);
}

/* Card 2：霓虹描边强调 */
.usecases__card:nth-child(2) {
  background-color: var(--yfb-surface-2);
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow-soft);
}

.usecases__card:nth-child(2) .usecases__title,
.usecases__card:nth-child(2) .usecases__desc {
  color: var(--yfb-ink);
}

/* Card 3：深色 tone */
.usecases__card:nth-child(3) {
  background-color: var(--yfb-tone-accent);
  color: var(--yfb-ink);
}

.usecases__card:nth-child(3) .usecases__title,
.usecases__card:nth-child(3) .usecases__desc {
  color: var(--yfb-ink);
}

/* 图标：浅底卡=默认（深底 accent 图标）；渐变卡=半透明深底 */
.usecases__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.usecases__card:nth-child(1) .usecases__icon {
  background-color: rgba(11, 11, 16, 0.25);
  color: var(--yfb-on-accent);
  border-color: rgba(10, 10, 18, 0.3);
}

.usecases__card:nth-child(2) .usecases__icon {
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border-color: var(--yfb-accent);
}

.usecases__card:nth-child(3) .usecases__icon {
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border-color: var(--yfb-rule);
}

.usecases__title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-lg);
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0;
  line-height: 1.2;
}

.usecases__desc {
  font-size: var(--yfb-fs-base);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  margin: 0;
  color: var(--yfb-text-secondary);
}

.usecases__card:nth-child(1) .usecases__desc {
  color: var(--yfb-on-accent);
  opacity: 0.9;
}

.usecases__card:nth-child(2) .usecases__desc,
.usecases__card:nth-child(3) .usecases__desc {
  color: var(--yfb-text-secondary);
}

.usecases__num {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-family: var(--yfb-font-heading);
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  opacity: 0.18;
  color: var(--yfb-accent);
}

.usecases__card:nth-child(1) .usecases__num {
  color: var(--yfb-on-accent);
}

@media (max-width: 1023px) {
  .usecases {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .usecases {
    grid-template-columns: 1fr;
  }
}
</style>